import { useEffect } from "react";
import axios from "axios";
import { buildPattern, visibleText } from "@/hooks/usePageContentOverrides";
import { sectionToHtml, INLINE_CLASSES, END_CLASSES } from "@/utils/sectionMarkup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cache = new Map();
const CANDIDATES = "h1,h2,h3,h4,h5,h6,p,li,blockquote,td,dt,dd,figcaption,span,div";

// Most specific visible element whose text contains the anchor, lifted to its block.
function findAnchorBlock(anchorText) {
  const pattern = buildPattern(anchorText);
  if (!pattern) return null;
  let best = null;
  document.querySelectorAll(`main ${CANDIDATES.split(",").join(", main ")}`).forEach((el) => {
    if (el.closest("[data-speicher-section]")) return;
    const txt = visibleText(el);
    pattern.lastIndex = 0;
    if (!pattern.test(txt)) return;
    if (!best || txt.length < best.len) best = { el, len: txt.length };
  });
  if (!best) return null;
  const el = best.el;
  return el.closest("ul,ol") || el.closest("p,h1,h2,h3,h4,h5,h6,blockquote,li,table,dl,figure") || el;
}

function buildNode(section, atEnd) {
  const node = document.createElement("section");
  node.dataset.speicherSection = section.id;
  node.setAttribute("data-testid", `speicher-section-${section.id}`);
  if (atEnd) {
    node.className = "bg-white border-t border-[#E2E8F0] py-12 md:py-16";
    node.innerHTML = `<div class="max-w-4xl mx-auto px-6 md:px-12">${sectionToHtml(section, END_CLASSES)}</div>`;
  } else {
    node.className = "my-8";
    node.innerHTML = sectionToHtml(section, INLINE_CLASSES);
  }
  return node;
}

function placeSection(section, allowFallback) {
  const existing = document.querySelector(`[data-speicher-section="${section.id}"]`);
  if (existing && !existing.dataset.fallback) return;
  const main = document.querySelector("main");
  if (!main) return;
  if (section.placement !== "end" && section.anchor_text) {
    const block = findAnchorBlock(section.anchor_text);
    if (block) {
      if (existing) existing.remove();
      block.insertAdjacentElement(section.placement === "before" ? "beforebegin" : "afterend", buildNode(section, false));
      return;
    }
    if (existing || !allowFallback) return;
    const node = buildNode(section, true);
    node.dataset.fallback = "1";
    main.appendChild(node);
    return;
  }
  if (!existing) main.appendChild(buildNode(section, true));
}

// Inserts Speicher-authored sections (approved) into the live page.
export default function usePageSections(path) {
  useEffect(() => {
    if (!path) return undefined;
    let cancelled = false;
    let observer = null;
    const timers = [];

    const apply = (sections) => {
      if (!sections.length) return;
      const run = (fallback) => !cancelled && sections.forEach((s) => placeSection(s, fallback));
      [0, 400, 1000, 2000].forEach((ms) => timers.push(setTimeout(() => run(false), ms)));
      timers.push(setTimeout(() => run(true), 3500));
      let scheduled = false;
      observer = new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => { scheduled = false; run(false); });
      });
      observer.observe(document.body, { childList: true, subtree: true });
      timers.push(setTimeout(() => { if (observer) observer.disconnect(); observer = null; }, 20000));
    };

    if (cache.has(path)) {
      apply(cache.get(path));
    } else {
      axios.get(`${API}/seo-public/sections`, { params: { path } })
        .then((r) => { cache.set(path, r.data || []); if (!cancelled) apply(r.data || []); })
        .catch(() => cache.set(path, []));
    }

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      if (observer) observer.disconnect();
      document.querySelectorAll("[data-speicher-section]").forEach((n) => n.remove());
    };
  }, [path]);
}
